import React, { useRef, useEffect } from 'react';
import { View, StyleSheet, Text, Dimensions } from 'react-native';
import { GoogleMap, LoadScript, Marker, OverlayViewF, OVERLAY_MOUSE_TARGET } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../../config/maps';

// Dark mode map styles for Google Maps
const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.business",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#263c3f" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b9a76" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca5b3" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }],
  },
  {
    featureType: "transit",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#515c6d" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#17263c" }],
  },
];

// Light mode map styles
const lightMapStyles = [
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.business',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'transit',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ lightness: 50 }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.stroke',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [{ lightness: 10 }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ lightness: 15 }],
  },
];

// Styles to hide street/road labels (append when showStreetLabels is false)
const hideStreetLabelsStyles = [
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

const getMapStyles = (isDarkMode, showStreetLabels) => {
  const base = isDarkMode ? darkMapStyles : lightMapStyles;
  if (showStreetLabels) return base;
  return [...base, ...hideStreetLabelsStyles];
};

// Inject floating animation CSS once
const injectFloatingAnimation = () => {
  if (typeof document !== 'undefined' && !document.getElementById('marker-float-animation')) {
    const style = document.createElement('style');
    style.id = 'marker-float-animation';
    style.textContent = `
      @keyframes markerFadeInUp {
        0% {
          opacity: 0;
          transform: translateY(10px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes markerFadeOutDown {
        0% {
          opacity: 1;
          transform: translateY(0);
        }
        100% {
          opacity: 0;
          transform: translateY(10px);
        }
      }
      @keyframes markerFloat {
        0%, 100% {
          transform: translateY(0px);
        }
        50% {
          transform: translateY(-6px);
        }
      }
      @keyframes shadowFadeIn {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
      @keyframes shadowPulse {
        0%, 100% {
          transform: translateX(-50%) scale(1);
          opacity: 0.3;
        }
        50% {
          transform: translateX(-50%) scale(0.7);
          opacity: 0.2;
        }
      }
      @keyframes clusterGlow {
        0%, 100% {
          transform: translate(-50%, -50%) scale(1);
          opacity: 0.15;
        }
        50% {
          transform: translate(-50%, -50%) scale(1.2);
          opacity: 0.08;
        }
      }
    `;
    document.head.appendChild(style);
  }
};

// Inject CSS to hide Google Maps attribution elements and disable selection
const injectMapStyleOverrides = () => {
  if (typeof document !== 'undefined' && !document.getElementById('map-style-overrides')) {
    const style = document.createElement('style');
    style.id = 'map-style-overrides';
    style.textContent = `
      /* Hide Google Maps copyright and attribution elements */
      .gmnoprint,
      .gm-style-cc,
      .gm-style a[href^="https://maps.google.com/maps"],
      .gm-style-cc + div {
        display: none !important;
      }
      
      /* Disable text selection on map markers and overlays */
      .gm-style {
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
      }
      
      /* Allow selection in popup containers */
      .map-popup-content,
      .map-popup-content * {
        user-select: text;
        -webkit-user-select: text;
        -moz-user-select: text;
        -ms-user-select: text;
      }
    `;
    document.head.appendChild(style);
  }
};

const MapView = ({
  region,
  onRegionChange,
  children,
  showsUserLocation,
  style,
  initialRegion,
  mapRef: externalMapRef, // Accept external mapRef
  isDarkMode = false, // Dark mode prop
  showStreetLabels = false, // When false, hide street names on map
  draggable = true, // When false, map cannot be panned (e.g. when popup is open on mobile)
  onDragStart,
  onDragEnd,
  onZoomChange, // Callback for zoom level changes
  onIdle, // Callback when map finishes panning/zooming
  onMapReady, // Called when the map has finished loading
  skipCenterSyncForPopupOpen = false, // When true, don't pass center so map isn't re-centered when popup opens (avoids overlay shift)
}) => {
  const internalMapRef = useRef(null);
  const mapRef = externalMapRef || internalMapRef; // Use external ref if provided
  const [userHasInteracted, setUserHasInteracted] = React.useState(false);
  const isDraggingRef = React.useRef(false); // Track if map is currently being dragged

  // Inject CSS to hide Google Maps attribution elements
  useEffect(() => {
    injectMapStyleOverrides();
  }, []);

  // Only calculate center/zoom if not dragging and user hasn't interacted
  // This prevents React from trying to control the map during drag operations
  // When skipCenterSyncForPopupOpen is true, don't pass center so the library doesn't call setCenter (avoids popup overlay shift on open)
  const center = React.useMemo(() => {
    if (skipCenterSyncForPopupOpen || isDraggingRef.current || userHasInteracted) {
      return undefined; // Don't control center during drag or after interaction or when opening popup
    }
    return region
      ? { lat: region.latitude, lng: region.longitude }
      : initialRegion
      ? { lat: initialRegion.latitude, lng: initialRegion.longitude }
      : { lat: 37.78825, lng: -122.4324 };
  }, [region, initialRegion, userHasInteracted, skipCenterSyncForPopupOpen]);

  const zoom = React.useMemo(() => {
    if (isDraggingRef.current || userHasInteracted) {
      return undefined; // Don't control zoom during drag or after interaction
    }
    return region
      ? Math.round(Math.log(360 / region.latitudeDelta) / Math.LN2)
      : 18; // Default zoom level for map detail
  }, [region, userHasInteracted]);

  const containerStyle = {
    width: '100%',
    height: '100%',
  };

  // Memoize options to prevent unnecessary re-renders during drag
  const options = React.useMemo(() => ({
    disableDefaultUI: true, // Disable default UI, we'll create custom controls
    zoomControl: false,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false, // Hide fullscreen button
    tilt: 0, // No tilt for better performance
    heading: 0, // Keep north at top
    maxZoom: 19, // Cap maximum zoom in level
    minZoom: 3, // Allow zooming out freely (min is around 1-3)
    gestureHandling: draggable ? 'greedy' : 'none', // When popup open on mobile, disable all gestures (pan + zoom)
    draggable, // When false, disables map pan (e.g. popup open on mobile)
    clickableIcons: false, // Disable default tooltips when clicking map labels (e.g. "Don Mills", POIs)
    styles: getMapStyles(isDarkMode, showStreetLabels),
  }), [isDarkMode, showStreetLabels, draggable]);

  const [loadError, setLoadError] = React.useState(null);
  const [isScriptLoaded, setIsScriptLoaded] = React.useState(false);

  // Handle LoadScript errors
  const handleLoadError = React.useCallback((error) => {
    console.error('Google Maps LoadScript error:', error);
    setLoadError('Failed to load Google Maps. Please check your internet connection and try again.');
  }, []);

  // Handle LoadScript success
  const handleLoadSuccess = React.useCallback(() => {
    setIsScriptLoaded(true);
    setLoadError(null);
  }, []);

  // Show error message if LoadScript fails
  if (loadError) {
    return (
      <View style={[styles.container, style, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
        <Text style={{ color: '#ff6b6b', fontSize: 16, textAlign: 'center', marginBottom: 10 }}>
          {loadError}
        </Text>
        <Text style={{ color: '#888', fontSize: 12, textAlign: 'center' }}>
          If this persists, please check your Google Maps API key configuration.
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, style]}>
      <LoadScript 
        googleMapsApiKey={GOOGLE_MAPS_CONFIG.apiKey}
      >
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={zoom}
          options={options}
          onDragStart={() => {
            isDraggingRef.current = true;
            setUserHasInteracted(true);
            if (onDragStart) {
              onDragStart();
            }
          }}
          onDragEnd={() => {
            // Use setTimeout to ensure drag end is processed before allowing updates
            setTimeout(() => {
              isDraggingRef.current = false;
            }, 0);
            if (onDragEnd) {
              onDragEnd();
            }
          }}
          onIdle={() => {
            // Don't fire onIdle during active drag - wait until drag is fully complete
            if (!isDraggingRef.current && onIdle) {
              onIdle();
            }
          }}
          onLoad={(map) => {
            mapRef.current = map;
            if (onMapReady) onMapReady();

            // Add zoom change listener - only if google is loaded
            if (onZoomChange && typeof window !== 'undefined' && window.google && window.google.maps) {
              try {
                map.addListener('zoom_changed', () => {
                  const newZoom = map.getZoom();
                  onZoomChange(newZoom);
                });
                // Also fire initial zoom level
                onZoomChange(map.getZoom());
              } catch (error) {
                console.error('Error setting up zoom listener:', error);
              }
            }
          }}
        >
          {children}
        </GoogleMap>
      </LoadScript>
    </View>
  );
};

const isMobileViewport = () => (typeof window !== 'undefined' && Dimensions.get('window').width < 768);
const prefersReducedMotion = () => (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
const MOBILE_MARKER_SCALE = 0.75;

// Set z-index on the OverlayView wrapper div (the parent of our content div).
// OverlayView creates its own container; CSS z-index on our inner div has no
// effect on stacking between different OverlayViews, so we walk up to the
// wrapper and apply it there.
const setOverlayZIndex = (el, zIndex) => {
  if (!el) return;
  // Walk up to find the OverlayView wrapper (position: absolute div in the pane)
  let container = el.parentElement;
  while (container && container.style.position !== 'absolute') {
    container = container.parentElement;
  }
  if (container) {
    container.style.zIndex = zIndex;
  }
};

// Scale markers up when zoomed in (1-2 levels before max zoom). Google Maps max ~21.
const getZoomScale = (zoomLevel) => {
  if (zoomLevel == null || zoomLevel < 19) return 1;
  return 1 + Math.min(zoomLevel - 18, 3) * 0.08; // zoom 19: 1.08, 20: 1.16, 21+: 1.24
};

// Web-specific Marker component - memoized to reduce re-renders when parent updates
// Throttled render counter — logs at most once per 500ms across all markers
let _lastMarkerRenderLogTime = 0;
let _markerRenderCount = 0;
const _logMarkerRender = (markerType) => {
  _markerRenderCount++;
  const now = Date.now();
  if (now - _lastMarkerRenderLogTime > 500) {
    _lastMarkerRenderLogTime = now;
    _markerRenderCount = 0;
  }
};

const MapMarkerInner = ({ coordinate, title, description, pinColor, userData, onPress, children, isHotspot, customMarker, hotspotScale, activityLevel, isTournament, tournament, isCluster, clusterCount, clusterHotspots, isCityMarker, isPlayerCluster, unreadCount, draggable, onDragEnd, isExiting, zoomLevel }) => {
  const zoomScale = getZoomScale(zoomLevel);
  const isMobile = isMobileViewport();
  const mobileScale = isMobile ? MOBILE_MARKER_SCALE : 1;
  const reduceMotion = prefersReducedMotion();
  const position = { lat: coordinate.latitude, lng: coordinate.longitude };
  _logMarkerRender(isTournament ? 'tournament' : isHotspot ? 'hotspot' : isCluster ? 'cluster' : userData ? 'user' : 'you');
  const [profileImageUrl, setProfileImageUrl] = React.useState(null);
  
  // Generate a random animation delay for variety (memoized per marker)
  const animationDelay = React.useRef(Math.random() * 2).current;
  
  // Run entry animation only once per mount; after that use 'none' so pan/zoom don't re-trigger it
  const [entryAnimationDone, setEntryAnimationDone] = React.useState(false);
  React.useEffect(() => {
    if (reduceMotion || isExiting) return;
    const t = setTimeout(() => setEntryAnimationDone(true), 400);
    return () => clearTimeout(t);
  }, [reduceMotion, isExiting]);
  
  // Track drag vs click for better UX
  const mouseDownPos = React.useRef(null);
  const isDragging = React.useRef(false);

  // Inject floating animation CSS on mount
  React.useEffect(() => {
    injectFloatingAnimation();
  }, []);

  const profileImageFallbackTried = React.useRef(false);
  // Load profile image if available (thumbnail first; sign-up uploads only have single file, so we fallback to original in onError)
  React.useEffect(() => {
    profileImageFallbackTried.current = false;
    const loadProfileImage = async () => {
      if (userData?.profilePictureUrl) {
        try {
          const { getProfilePictureUrl } = await import('../../services/storage');
          const url = getProfilePictureUrl(userData.profilePictureUrl, 'thumbnail');
          setProfileImageUrl(url);
        } catch (error) {
          console.error('Error loading profile image for marker:', error);
          setProfileImageUrl(null);
        }
      }
    };
    if (userData) {
      loadProfileImage();
    }
  }, [userData?.profilePictureUrl]);

  const handleMarkerImageError = React.useCallback(async () => {
    if (!userData?.profilePictureUrl || profileImageFallbackTried.current) return;
    profileImageFallbackTried.current = true;
    try {
      const { getProfilePictureUrl } = await import('../../services/storage');
      const url = getProfilePictureUrl(userData.profilePictureUrl, 'large');
      if (url) setProfileImageUrl(url);
    } catch (_) {}
  }, [userData?.profilePictureUrl]);

  // For tournament markers
  if (isTournament && tournament && typeof window !== 'undefined' && window.google && window.google.maps) {
    const TournamentMarker = require('../TournamentMarker').default;
    const participantCount = tournament.participants?.items?.length || 0;
    
    // Handle pointer events to distinguish click from drag (don't stopPropagation on down so map can pan)
    const handleTournamentPointerDown = (e) => {
      mouseDownPos.current = {
        x: e.clientX || e.touches?.[0]?.clientX || 0,
        y: e.clientY || e.touches?.[0]?.clientY || 0,
      };
      isDragging.current = false;
    };
    
    const handleTournamentPointerMove = (e) => {
      if (!mouseDownPos.current) return;
      
      const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
      const currentY = e.clientY || e.touches?.[0]?.clientY || 0;
      
      const deltaX = currentX - mouseDownPos.current.x;
      const deltaY = currentY - mouseDownPos.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      if (distance > 5) {
        isDragging.current = true;
      }
    };
    
    const handleTournamentPointerUp = (e) => {
      if (!isDragging.current && onPress) {
        e.stopPropagation();
        onPress(e);
      }
      mouseDownPos.current = null;
      isDragging.current = false;
    };
    
    const handleTournamentPointerLeave = () => {
      if (mouseDownPos.current) {
        isDragging.current = true;
      }
    };
    
    const tw = 70 * zoomScale * mobileScale;
    const th = 95 * zoomScale * mobileScale;
    // Southern markers (lower on map) get higher z-index so they cover northern markers' shadows
    const markerZ = 500 + Math.round((90 - coordinate.latitude) * 20);
    return (
      <OverlayViewF
        position={position}
        mapPaneName={OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={() => ({
          x: -tw / 2,
          y: -th,
        })}
      >
        <div
          ref={(el) => setOverlayZIndex(el, markerZ)}
          style={{
            width: tw,
            height: th,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            ...(isExiting ? {
              transformOrigin: 'bottom center',
              animation: 'markerFadeOutDown 0.35s ease-in both',
              willChange: 'transform, opacity',
            } : {
              transformOrigin: 'bottom center',
              animation: reduceMotion ? 'none' : (entryAnimationDone ? 'none' : 'markerFadeInUp 0.35s ease-out both'),
              animationDelay: reduceMotion ? undefined : (entryAnimationDone ? undefined : `${animationDelay * 0.15}s`),
            }),
          }}
          onMouseDown={handleTournamentPointerDown}
          onMouseMove={handleTournamentPointerMove}
          onMouseUp={handleTournamentPointerUp}
          onMouseLeave={handleTournamentPointerLeave}
          onTouchStart={handleTournamentPointerDown}
          onTouchMove={handleTournamentPointerMove}
          onTouchEnd={handleTournamentPointerUp}
          onTouchCancel={handleTournamentPointerLeave}
        >
          <div style={{ transform: `scale(${zoomScale * mobileScale})`, transformOrigin: 'bottom center', width: 70, height: 95 }}>
          <TournamentMarker 
            tournament={tournament} 
            onClick={(e) => {
              // Prevent the click from propagating if we're dragging
              if (isDragging.current) {
                e.stopPropagation();
              }
            }} 
            participantCount={participantCount} 
            showShadowAnimation={false}
          />
          </div>
        </div>
      </OverlayViewF>
    );
  }

  // For cluster markers (grouped hotspots at lower zoom levels)
  if (isCluster && typeof window !== 'undefined' && window.google && window.google.maps) {
    // Pin marker style - sized based on cluster count, scaled by zoom when zoomed in
    const baseClusterSize = isCityMarker ? 90 : Math.min(60 + Math.log(clusterCount) * 10, 85);
    const clusterSize = Math.round(baseClusterSize * zoomScale * mobileScale);
    const cityName = title || 'City'; // Get city name from title prop
    
    // Helper function to add subtle color variation
    // Uses position hash to generate consistent but varied colors
    const addColorVariation = (baseColor, variationSeed) => {
      // Convert hex to RGB
      const hex = baseColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Generate subtle variation (-15 to +15 for each channel)
      const variation = (variationSeed % 31) - 15;
      
      // Apply variation (more to green channel for greens, more to red for reds)
      const isGreen = g > r && g > b;
      const isRed = r > g && r > b;
      
      let newR = r;
      let newG = g;
      let newB = b;
      
      if (isGreen) {
        // Vary green channel more for green clusters
        newG = Math.max(0, Math.min(255, g + variation * 1.5));
        newR = Math.max(0, Math.min(255, r + variation * 0.5));
        newB = Math.max(0, Math.min(255, b + variation * 0.5));
      } else if (isRed) {
        // Vary red channel more for red clusters
        newR = Math.max(0, Math.min(255, r + variation * 1.5));
        newG = Math.max(0, Math.min(255, g + variation * 0.5));
        newB = Math.max(0, Math.min(255, b + variation * 0.5));
      } else {
        // General variation
        newR = Math.max(0, Math.min(255, r + variation));
        newG = Math.max(0, Math.min(255, g + variation));
        newB = Math.max(0, Math.min(255, b + variation));
      }
      
      // Convert back to hex
      return '#' + [newR, newG, newB].map(x => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      }).join('');
    };
    
    // Generate variation seed from cluster position and count
    const variationSeed = Math.floor(
      (coordinate.latitude * 1000 + coordinate.longitude * 1000 + clusterCount) % 1000
    );
    
    // Base colors - using exact colors specified
    const baseColors = (() => {
      if (isCityMarker) {
        return {
          primary: '#84D288', // Green
          secondary: '#84D288', // Same green for consistency
          accent: '#84D288',
        };
      }
      if (isPlayerCluster) {
        return { primary: '#5B9BD5', secondary: '#5B9BD5', accent: '#5B9BD5' }; // Blue for player clusters
      }
      // Smaller clusters use red, larger ones use green
      return {
        primary: clusterCount > 20 ? '#84D288' : '#EF5350', // Green or red
        secondary: clusterCount > 20 ? '#84D288' : '#EF5350',
        accent: clusterCount > 20 ? '#84D288' : '#EF5350',
      };
    })();
    
    // Apply subtle variations
    const colors = {
      primary: addColorVariation(baseColors.primary, variationSeed),
      secondary: addColorVariation(baseColors.secondary, variationSeed + 1),
      accent: addColorVariation(baseColors.accent, variationSeed + 2),
    };
    
    // Border color - lighter version of the marker color
    const getBorderColor = () => {
      if (isPlayerCluster) return '#7AB3E8';
      if (isCityMarker || clusterCount > 20) {
        // Light green border for green clusters (slightly lighter than #84D288)
        return '#94EB98'; // Similar to what's used in quick pair overlays
      } else {
        // Light red border for red clusters (lighter than #E53935)
        return '#EF5350'; // Lighter red variant
      }
    };
    
    const borderColor = getBorderColor();
    
    // Determine if this is a green marker (for z-index)
    const isGreenMarker = isCityMarker || isPlayerCluster || clusterCount > 20;
    // Green markers have higher z-index to appear on top of red ones
    const markerZIndex = isGreenMarker ? 100 : 50;
    
    // Handle pointer events for clusters
    const handleClusterPointerDown = (e) => {
      mouseDownPos.current = {
        x: e.clientX || e.touches?.[0]?.clientX || 0,
        y: e.clientY || e.touches?.[0]?.clientY || 0,
      };
      isDragging.current = false;
    };
    
    const handleClusterPointerMove = (e) => {
      if (!mouseDownPos.current) return;
      
      const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
      const currentY = e.clientY || e.touches?.[0]?.clientY || 0;
      
      const deltaX = currentX - mouseDownPos.current.x;
      const deltaY = currentY - mouseDownPos.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      if (distance > 5) {
        isDragging.current = true;
      }
    };
    
    const handleClusterPointerUp = (e) => {
      if (!isDragging.current && onPress) {
        e.stopPropagation();
        onPress(e);
      }
      mouseDownPos.current = null;
      isDragging.current = false;
    };
    
    const handleClusterPointerLeave = () => {
      if (mouseDownPos.current) {
        isDragging.current = true;
      }
    };
    
    // Generate unique ID for SVG gradients
    const uniqueId = React.useRef(`cluster-${Math.random().toString(36).substr(2, 9)}`).current;
    const gradientId = `gradient-${uniqueId}`;
    const shadowId = `shadow-${uniqueId}`;
    
    // Inject the circle pulse animations if not already present
    React.useEffect(() => {
      if (typeof document !== 'undefined' && !document.getElementById('cluster-circle-animation')) {
        const style = document.createElement('style');
        style.id = 'cluster-circle-animation';
        style.textContent = `
          @keyframes clusterGlow {
            0%, 100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.12;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.2);
              opacity: 0.06;
            }
          }
        `;
        document.head.appendChild(style);
      }
    }, []);
    
    return (
      <OverlayViewF
        position={position}
        mapPaneName={OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={() => ({
          x: -clusterSize / 2,
          y: isCityMarker ? -clusterSize : -clusterSize / 2,
        })}
      >
        <div
          style={{
            width: `${clusterSize}px`,
            height: isCityMarker ? `${clusterSize + 20}px` : `${clusterSize}px`,
            cursor: 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            position: 'relative',
            zIndex: markerZIndex,
            ...(isExiting ? {
              transformOrigin: 'bottom center',
              animation: 'markerFadeOutDown 0.35s ease-in both',
              willChange: 'transform, opacity',
            } : {
              transformOrigin: 'bottom center',
              animation: reduceMotion ? 'none' : (entryAnimationDone ? 'none' : 'markerFadeInUp 0.35s ease-out both'),
              animationDelay: reduceMotion ? undefined : (entryAnimationDone ? undefined : `${animationDelay * 0.15}s`),
            }),
          }}
          onMouseDown={handleClusterPointerDown}
          onMouseMove={handleClusterPointerMove}
          onMouseUp={handleClusterPointerUp}
          onMouseLeave={handleClusterPointerLeave}
          onTouchStart={handleClusterPointerDown}
          onTouchMove={handleClusterPointerMove}
          onTouchEnd={handleClusterPointerUp}
          onTouchCancel={handleClusterPointerLeave}
        >
          {/* Cluster marker with pin shape and animated circles background */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              transition: 'transform 0.2s ease, filter 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.15)';
              e.currentTarget.style.filter = 'brightness(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.filter = 'brightness(1)';
            }}
          >
            {/* Subtle animated circle background - very subtle */}
            <div
              style={{
                position: 'absolute',
                top: `${isCityMarker ? clusterSize * 0.27 : clusterSize * 0.27}px`,
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: `${clusterSize * 1.3}px`,
                height: `${clusterSize * 1.3}px`,
                borderRadius: '50%',
                backgroundColor: colors.primary,
                opacity: 0.12,
                pointerEvents: 'none',
                animation: reduceMotion ? 'none' : 'clusterGlow 5s ease-in-out infinite',
                zIndex: 0,
              }}
            />
            
            {/* Pin marker SVG - matches hotspot marker style */}
            <svg 
              width={clusterSize} 
              height={isCityMarker ? clusterSize + 20 : clusterSize} 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 44 60" 
              style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                zIndex: 2,
              }}
            >
              {/* Base pin color - same style as hotspot markers */}
              <path 
                d="M 22 0 C 13 0 6 7 6 16 C 6 22 9 27 22 52 C 35 27 38 22 38 16 C 38 7 31 0 22 0 Z" 
                fill={colors.primary} 
                stroke="none"
              />
            </svg>
            
            {/* Count text - positioned higher on pin, white text, no circle */}
            <div
              style={{
                position: 'absolute',
                // Position higher - around where the white circle center would be (y=16 in viewBox)
                // 16/60 = 0.267 of the height, but we'll move it up a bit more
                top: `${Math.round((14 / 60) * clusterSize)}px`,
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
                zIndex: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span
                style={{
                  color: 'white',
                  fontWeight: '900',
                  fontSize: isCityMarker ? '20px' : clusterCount > 99 ? '15px' : '18px',
                  textShadow: '0 2px 6px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.4)',
                  display: 'block',
                  lineHeight: 1,
                  letterSpacing: '-0.5px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
              >
                {clusterCount}
              </span>
              {isCityMarker && (
                <span
                  style={{
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '700',
                    display: 'block',
                    marginTop: '2px',
                    textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.3)',
                    lineHeight: 1.2,
                    opacity: 0.95,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  }}
                >
                  {cityName}
                </span>
              )}
            </div>
          </div>
        </div>
      </OverlayViewF>
    );
  }

  // For hotspot markers
  if (isHotspot && customMarker && typeof window !== 'undefined' && window.google && window.google.maps) {
    const HotspotMarker = require('../HotspotMarker').default;
    // Calculate scaled dimensions for positioning (combine hotspot activity scale + zoom scale)
    const scale = (hotspotScale || 1.0) * zoomScale * mobileScale;
    const scaledWidth = Math.round(66 * scale);
    const scaledHeight = Math.round(90 * scale);
    
    // Handle pointer events to distinguish click from drag (don't stopPropagation on down so map can pan)
    const handleHotspotPointerDown = (e) => {
      mouseDownPos.current = {
        x: e.clientX || e.touches?.[0]?.clientX || 0,
        y: e.clientY || e.touches?.[0]?.clientY || 0,
      };
      isDragging.current = false;
    };
    
    const handleHotspotPointerMove = (e) => {
      if (!mouseDownPos.current) return;
      
      const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
      const currentY = e.clientY || e.touches?.[0]?.clientY || 0;
      
      const deltaX = currentX - mouseDownPos.current.x;
      const deltaY = currentY - mouseDownPos.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      if (distance > 5) {
        isDragging.current = true;
      }
    };
    
    const handleHotspotPointerUp = (e) => {
      if (!isDragging.current && onPress) {
        e.stopPropagation();
        onPress(e);
      }
      mouseDownPos.current = null;
      isDragging.current = false;
    };
    
    const handleHotspotPointerLeave = () => {
      if (mouseDownPos.current) {
        isDragging.current = true;
      }
    };
    
    // Southern markers (lower on map) get higher z-index so they cover northern markers' shadows
    const markerZ = 500 + Math.round((90 - coordinate.latitude) * 20);
    return (
      <OverlayViewF
        position={position}
        mapPaneName={OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={() => ({
          x: -scaledWidth / 2,  // Half of scaled width
          y: -scaledHeight,     // Anchor at bottom
        })}
      >
        <div
          ref={(el) => setOverlayZIndex(el, markerZ)}
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            transformOrigin: 'bottom center',
            // Entry animation only on first mount; entryAnimationDone prevents re-trigger on pan/zoom
            animation: reduceMotion ? 'none' : (isExiting ? 'markerFadeOutDown 0.35s ease-in both' : (entryAnimationDone ? 'none' : 'markerFadeInUp 0.35s ease-out both')),
            animationDelay: reduceMotion ? undefined : (isExiting ? '0s' : (entryAnimationDone ? undefined : `${animationDelay * 0.15}s`)),
            willChange: isExiting ? 'transform, opacity' : undefined,
          }}
          onMouseDown={handleHotspotPointerDown}
          onMouseMove={handleHotspotPointerMove}
          onMouseUp={handleHotspotPointerUp}
          onMouseLeave={handleHotspotPointerLeave}
          onTouchStart={handleHotspotPointerDown}
          onTouchMove={handleHotspotPointerMove}
          onTouchEnd={handleHotspotPointerUp}
          onTouchCancel={handleHotspotPointerLeave}
        >
          <HotspotMarker hotspot={customMarker} onClick={(e) => {
            // Prevent the click from propagating if we're dragging
            if (isDragging.current) {
              e.stopPropagation();
            }
          }} scale={scale} activityLevel={activityLevel} showShadowAnimation={false} />
        </div>
      </OverlayViewF>
    );
  }

  // For custom HTML markers with userData (player/club pins)
  if (userData && typeof window !== 'undefined' && window.google && window.google.maps) {
    const userMarkerW = 66 * zoomScale * mobileScale;
    const userMarkerH = 90 * zoomScale * mobileScale;
    // Player and club markers always on top of hotspots/tournaments; within type, south = higher z-index
    const markerZ = 5000 + Math.round((90 - coordinate.latitude) * 20);
    const handlePointerDown = (e) => {
      mouseDownPos.current = {
        x: e.clientX || e.touches?.[0]?.clientX || 0,
        y: e.clientY || e.touches?.[0]?.clientY || 0,
      };
      isDragging.current = false;
    };
    
    // Handle mouse/touch move - detect drag
    const handlePointerMove = (e) => {
      if (!mouseDownPos.current) return;
      
      const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
      const currentY = e.clientY || e.touches?.[0]?.clientY || 0;
      
      const deltaX = currentX - mouseDownPos.current.x;
      const deltaY = currentY - mouseDownPos.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      // If moved more than 5 pixels, consider it a drag
      if (distance > 5) {
        isDragging.current = true;
      }
    };
    
    // Handle mouse/touch up - trigger click only if not dragging
    const handlePointerUp = (e) => {
      if (!isDragging.current && onPress) {
        e.stopPropagation(); // Prevent event from reaching map
        onPress(e);
      }
      mouseDownPos.current = null;
      isDragging.current = false;
    };
    
    // Handle pointer leaving marker - cancel tracking
    const handlePointerLeave = () => {
      if (mouseDownPos.current) {
        // If pointer leaves while tracking, consider it a drag/cancel
        isDragging.current = true;
      }
    };
    
    return (
      <OverlayViewF
        position={position}
        mapPaneName={OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={() => ({
          x: -userMarkerW / 2,
          y: -userMarkerH,
        })}
      >
        <div
          ref={(el) => setOverlayZIndex(el, markerZ)}
          style={{
            position: 'absolute',
            cursor: 'pointer',
            width: userMarkerW,
            height: userMarkerH,
            transformOrigin: 'bottom center', // Set anchor point at bottom center
            userSelect: 'none',
            WebkitUserSelect: 'none',
            // Entry animation only on first mount; entryAnimationDone prevents re-trigger on pan/zoom
            animation: reduceMotion ? 'none' : (isExiting ? 'markerFadeOutDown 0.35s ease-in both' : (entryAnimationDone ? 'none' : 'markerFadeInUp 0.35s ease-out both')),
            animationDelay: reduceMotion ? undefined : (isExiting ? '0s' : (entryAnimationDone ? undefined : `${animationDelay * 0.15}s`)),
            willChange: isExiting ? 'transform, opacity' : undefined,
          }}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerLeave}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          onTouchCancel={handlePointerLeave}
        >
          <div style={{ transform: `scale(${zoomScale * mobileScale})`, transformOrigin: 'bottom center', width: 66, height: 90, position: 'relative' }}>
          {/* Floating pin container */}
          <div
            style={{
              animation: reduceMotion ? 'none' : 'markerFloat 3s ease-in-out infinite',
              animationDelay: reduceMotion ? undefined : `${animationDelay}s`,
            }}
          >
            {/* Pin SVG with inner circle for profile picture */}
            <svg width="66" height="90" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 60" style={{ position: 'absolute', top: 0, left: 0 }}>
              <path d="M 22 0 C 13 0 6 7 6 16 C 6 22 9 27 22 52 C 35 27 38 22 38 16 C 38 7 31 0 22 0 Z" 
                    fill={pinColor} 
                    stroke="none"/>
              {/* White circle for profile picture background */}
              <circle cx="22" cy="16" r="11" fill="white" fillOpacity="0.95"/>
            </svg>
            
            {/* Profile Picture - Bigger, no border; onError fallback to original (sign-up single-file uploads have no thumbnail) */}
            <img
              src={profileImageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.username || userData.clubName || 'U')}&background=${pinColor.replace('#', '')}&color=fff&size=60&rounded=true`}
              alt={userData.username || userData.clubName}
              onError={userData?.profilePictureUrl ? handleMarkerImageError : undefined}
              style={{
                position: 'absolute',
                top: '9.5px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                border: 'none',
                objectFit: 'cover',
              }}
            />
            
            {/* Unread Count Badge */}
            {unreadCount > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  minWidth: '20px',
                  height: '20px',
                  borderRadius: '10px',
                  backgroundColor: '#FF4444',
                  border: '2px solid white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 6px',
                  zIndex: 10,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                <span
                  style={{
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '700',
                    lineHeight: '1',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              </div>
            )}
          </div>
          </div>
        </div>
      </OverlayViewF>
    );
  }
  
  // For "You" marker - simple circle
  const icon = pinColor && typeof window !== 'undefined' && window.google && window.google.maps ? {
    path: window.google.maps.SymbolPath.CIRCLE || 0,
    fillColor: pinColor,
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 3,
    scale: 12 * zoomScale * mobileScale,
  } : undefined;

  // "You" marker (no userData/hotspot/tournament/cluster) — keep on top with other players/clubs
  return (
    <Marker
      position={position}
      title={title}
      icon={icon}
      onClick={onPress}
      zIndex={5000}
      draggable={draggable || false}
      onDragEnd={onDragEnd ? (e) => {
        onDragEnd({
          latitude: e.latLng.lat(),
          longitude: e.latLng.lng(),
        });
      } : undefined}
    />
  );
};

export const MapMarker = React.memo(MapMarkerInner);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default MapView;

